import { Component, Input, SimpleChanges, ChangeDetectorRef, OnDestroy, OnChanges, AfterViewInit } from '@angular/core';
import { SLTargetsLimitsModal } from 'app/modules/cslo/models/cslo-sl-targets-limits-modal';
import * as Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import { secureRandom } from 'app/shared/util/secure-random.util';

export interface BubbleChartConfig {
  chartData: SLTargetsLimitsModal[];
  chartTitle?: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
  showAdjustLink?: boolean;
  adjustLinkText?: string;
  onAdjustClick?: (bubbleData: BubbleDataPoint) => void;
  legendEnabled?: boolean;
  customTooltip?: Highcharts.TooltipOptions;
  bubbleMaxSize?: number;
}

export interface BubbleDataPoint {
  item?: string | number;
  location?: string | number;
  x: number;
  y: number;
  z: number;
  serviceLevel: number;
  inventoryInvestment: number;
  totalDemand: number;
  department?: number;
  inventoryClass?: string;
  legend?: string;
}

@Component({
  selector: 'bubble',
  templateUrl: './bubble.component.html',
  styleUrl: './bubble.component.scss',
  standalone: false
})
export class BubbleComponent implements AfterViewInit, OnChanges, OnDestroy {
  private stickyTooltipInterval: any = null;
  @Input() config!: BubbleChartConfig;
  @Input() tabContext?: string;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  myChart!: Highcharts.Chart;
  @Input() type = '';
  chartContainerId = '';
  stickyTooltipActive = false;
  private viewInitialized = false;
  private pendingChartData: BubbleDataPoint[] | null = null;
  private outsideClickHandler: ((event: MouseEvent) => void) | null = null;

  constructor(private readonly _cdref: ChangeDetectorRef) {
    this.chartContainerId = 'sl-chart-container-' + secureRandom().toString(36).substr(2, 9);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.viewInitialized = true;
      if (this.config?.chartData && this.config.chartData.length > 0) {
        const bubbleData = this.mapToBubbleData(this.config?.chartData || []);
        this.initializeChart(bubbleData);
      } else if (this.pendingChartData) {
        this.initializeChart(this.pendingChartData);
        this.pendingChartData = null;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config?.chartData && this.config.chartData.length > 0) {
      const bubbleData = this.mapToBubbleData(this.config.chartData);

      if (!this.viewInitialized) {
        // View not ready yet, store data for later
        this.pendingChartData = bubbleData;
        return;
      }

      if (this.myChart) {
        this.myChart.destroy();
      }

      if (this.outsideClickHandler) {
        document.removeEventListener('mousedown', this.outsideClickHandler);
        this.outsideClickHandler = null;
      }

      this.initializeChart(bubbleData);
    }
  }

  private mapToBubbleData(data: SLTargetsLimitsModal[]): BubbleDataPoint[] {
    return data.map((row, index) => ({
      item: row['item'] ?? (row.group || `Item-${index}`),
      location: row['location'] ?? String(row.department || `Loc-${index}`),
      x: row['x'] ?? ((row['skuCount'] || 0) * 10 || index * 1000),
      y: row['y'] ?? 85 + secureRandom() * 10,
      z: row['z'] ?? ((row['skuCount'] || 0) * 15 || index * 1500),
      serviceLevel: row['serviceLevel'] ?? 85 + secureRandom() * 10,
      inventoryInvestment: row['inventoryInvestment'] ?? ((row['skuCount'] || 0) * 10 || index * 1000),
      totalDemand: row['totalDemand'] ?? ((row['skuCount'] || 0) * 15 || index * 1500)
    }));
  }

  private getClickHandler() {
    return function (this: any, event: any) {
      const chart = this.series.chart;
      const point = event.point;
      // Deselect previous bubble
      if (chart.selectedBubble && chart.selectedBubble !== point) {
        chart.selectedBubble.select(false, false);
      }
      point.select(true, false);
      chart.selectedBubble = point;
      if (chart.renderTo?.__bubbleComponentInstance) {
        const comp = chart.renderTo.__bubbleComponentInstance;
        comp.stickyTooltipActive = true;
        // Start interval to keep tooltip alive
        if (comp.stickyTooltipInterval) {
          clearInterval(comp.stickyTooltipInterval);
        }
        comp.stickyTooltipInterval = setInterval(() => {
          if (chart.tooltip && comp.stickyTooltipActive && chart.selectedBubble) {
            chart.tooltip.refresh(chart.selectedBubble);
          }
        }, 200);
      }
      if (chart.tooltip) {
        chart.tooltip.refresh(point);
      }
    };
  }

  private initializeChart(bubbleData: BubbleDataPoint[]): void {
    const xValues = bubbleData.map(d => d.x);
    const yValues = bubbleData.map(d => d.y);

    const xMin = 0;
    const xMax = Math.ceil(Math.max(...xValues) * 1.1);
    const yMin = Math.floor(Math.min(...yValues) * 0.95);
    const yMax = Math.ceil(Math.max(...yValues) * 1.05);
    const defaultTooltip = {
      useHTML: true,
      backgroundColor: 'white',
      borderColor: '#e0e5eb',
      borderRadius: 16,
      shadow: false,
      hideDelay: 300,
      stickOnContact: true,
      formatter: (function (config: BubbleChartConfig, tabContext?: string) {
        return function (this: any) {
          const point = this.point as any;
          const data = point.options as BubbleDataPoint;

          // Determine if this is group level or SKU location level
          const isGroupLevel = data.department && data.location && data.inventoryClass; //breakdown tab
          const isSkuLocationLevel = data.item && data.location; // overview tab

          // Build header content
          let headerContent = '';
          if (tabContext === 'overview') {
            headerContent = `${data.item}`;
          } else if (tabContext === 'breakdown') {
            if (isSkuLocationLevel) {
              headerContent = `ITEM - ${data.item}<br/>LOCATION - ${data.location}`;
            } else if (isGroupLevel) {
              headerContent = `DEPARTMENT - ${data.department}<br/>LOCATION - ${data.location}<br/>INVENTORY CLASS - ${data.inventoryClass}`;
            }
          } else if (data.item) {
            headerContent = `${data.item}`;
          } else {
            headerContent = 'Details';
          }

          // Determine service level label and format
          const serviceLevelLabel = isSkuLocationLevel ? 'Service Level' : 'Average Service Level';
          const serviceLevelValue = data.serviceLevel.toFixed(1) + '%';

          // Format currency without decimals
          const formatCurrency = (value: number) => {
            return '$' + Math.round(value).toLocaleString();
          };

          return `
              <div class="bubble-tooltip">
                <div class="tooltip-header">
                  ${headerContent}
                </div>
                <table class="tooltip-content">
                  <tr>
                    <td>${serviceLevelLabel}</td>
                    <td class="value">${serviceLevelValue}</td>
                  </tr>
                  <tr>
                    <td>Inventory Investment</td>
                    <td class="value">${formatCurrency(data.inventoryInvestment)}</td>
                  </tr>
                  <tr>
                    <td>Total Demand</td>
                    <td class="value">${formatCurrency(data.totalDemand)}</td>
                  </tr>
                </table>
                ${
                  config.showAdjustLink
                    ? `
                  <div class="tooltip-link">
                    <a href="javascript:void(0)" class="adjust-link">
                      ${config.adjustLinkText || 'Adjust on SKU Location Level'}
                    </a>
                  </div>
                `
                    : ''
                }
              </div>
            `;
        };
      })(this.config, this.tabContext),
      shared: false
    };
    this.chartOptions = {
      chart: {
        type: 'bubble',
        plotBorderWidth: 1,
        zooming: {
          type: 'xy'
        },
        backgroundColor: '#ffffff',
        margin: [20, 30, 60, 80],
        spacing: [10, 10, 15, 10],
        marginBottom: 70
      },
      title: {
        text: '',
        verticalAlign: 'bottom',
        style: {
          fontSize: '14px',
          fontWeight: '600',
          color: '#29333d'
        },
        align: 'center'
      },
      xAxis: {
        title: {
          text: this.config.xAxisTitle,
          style: {
            color: '#52667a',
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase',
            textAlign: 'center'
          },
          margin: 10
        },
        labels: {
          formatter: function () {
            const val = this.value as number;
            if (val >= 1000000000) {
              return (val / 1000000000).toFixed(1) + 'B';
            } else if (val >= 1000000) {
              return (val / 1000000).toFixed(1) + 'M';
            } else if (val >= 1000) {
              return (val / 1000).toFixed(0) + 'K';
            } else if (val > 0 && val < 1000) {
              return val.toFixed(1);
            }
            return val.toString();
          },
          style: {
            color: '#52667a',
            fontSize: '11px'
          }
        },
        gridLineWidth: 1,
        gridLineColor: '#e0e5eb',
        min: xMin,
        max: xMax,
        tickInterval: Math.ceil((xMax - xMin) / 5)
      },
      yAxis: {
        title: {
          text: this.config.yAxisTitle,
          style: {
            color: '#52667a',
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase'
          },
          margin: 12
        },
        labels: {
          formatter: function () {
            return Math.round(this.value as number) + '%';
          },
          style: {
            color: '#52667a',
            fontSize: '11px'
          }
        },
        gridLineColor: '#e0e5eb',
        min: yMin,
        max: yMax,
        tickInterval: 4
      },
      legend: {
        enabled: this.config.legendEnabled
      },
      tooltip: {
        ...defaultTooltip,
        ...(this.config.customTooltip || {})
      },
      plotOptions: {
        bubble: {
          minSize: 8,
          maxSize: this.config.bubbleMaxSize ?? 40,
          marker: {
            fillOpacity: 0.6,
            lineColor: 'transparent'
          },
          dataLabels: {
            enabled: false
          },
          cursor: 'pointer',
          stickyTracking: false,
          allowPointSelect: false,
          states: {
            hover: {
              enabled: false
            },
            select: {
              borderWidth: 0
            }
          },
          events: {
            click: function (event: any) {
              const chart = this.chart;
              const point = event.point;
              chart.getSelectedPoints().forEach((p: any) => {
                if (p !== point) p.select(false);
              });
              point.select(!point.selected, false);
            }
          }
        }
      },
      credits: {
        enabled: false
      }
    };
    if (this.type === 'Simulation Report') {
      // ...existing Simulation Report sticky tooltip logic...
      if (this.chartOptions.legend) {
        this.chartOptions.legend = {
          y: 22,
          align: 'center',
          verticalAlign: 'bottom',
          layout: 'horizontal',
          itemStyle: {
            fontWeight: '500'
          }
        };
      }
      (this.chartOptions.xAxis as Highcharts.XAxisOptions).title!.margin = 1;
      const currentData: any[] = (this.config.chartData || []).filter((d: any) => d.legend === 'Current');
      const simulationData: any[] = (this.config.chartData || []).filter((d: any) => d.legend === 'Simulation');
      this.chartOptions.series = [
        {
          type: 'bubble',
          name: 'Current',
          data: currentData,
          color: '#52667A',
          point: {
            events: {
              click: this.getClickHandler(),
              mouseOver: function () {
                const chart = this.series.chart;
                if ((chart as any).renderTo && (chart as any).renderTo.__bubbleComponentInstance && (chart as any).renderTo.__bubbleComponentInstance.stickyTooltipActive) {
                  // Disable tooltip on hover if sticky tooltip is active
                  return false;
                }
                return true;
              },
              mouseOut: function () {
                const chart = this.series.chart;
                if ((chart as any).renderTo && (chart as any).renderTo.__bubbleComponentInstance && (chart as any).renderTo.__bubbleComponentInstance.stickyTooltipActive) {
                  // Do nothing if stickyTooltipActive
                  return;
                }
                if (chart.tooltip) {
                  chart.tooltip.hide();
                }
              }
            }
          }
        },
        {
          type: 'bubble',
          name: 'Simulation',
          data: simulationData,
          color: '#3B98A8',
          point: {
            events: {
              click: this.getClickHandler(),
              mouseOver: function () {
                const chart = this.series.chart;
                if ((chart as any).renderTo && (chart as any).renderTo.__bubbleComponentInstance && (chart as any).renderTo.__bubbleComponentInstance.stickyTooltipActive) {
                  return false;
                }
                return true;
              },
              mouseOut: function () {
                const chart = this.series.chart;
                if ((chart as any).renderTo && (chart as any).renderTo.__bubbleComponentInstance && (chart as any).renderTo.__bubbleComponentInstance.stickyTooltipActive) {
                  // Do nothing if stickyTooltipActive
                  return;
                }
                if (chart.tooltip) {
                  chart.tooltip.hide();
                }
              }
            }
          }
        }
      ];
      // End Simulation Report block
    } else {
      this.chartOptions.series = [
        {
          type: 'bubble',
          name: 'Service Level',
          data: bubbleData.map(d => ({
            ...d,
            marker: {
              fillOpacity: 0.6,
              fillColor: '#3B98A8B2',
              states: {
                hover: {
                  enabled: true,
                  lineWidth: 0,
                  fillOpacity: 0.7
                }
              }
            }
          })),
          point: {
            events: {
              click: this.getClickHandler(),
              mouseOver: function (this: Highcharts.Point) {
                const chart = this.series.chart;

                // Check if sticky tooltip is active
                if ((chart as any).renderTo?.__bubbleComponentInstance?.stickyTooltipActive) {
                  return;
                }

                chart.series[0].points.forEach(point => {
                  point.update(
                    {
                      marker: {
                        fillColor:
                          point === this
                            ? 'rgba(59, 152, 168, 0.7)' // keep original
                            : 'rgba(59, 152, 168, 0.2)' // fade others
                      }
                    },
                    false
                  );
                });

                chart.redraw();
              },
              mouseOut: function (this: Highcharts.Point) {
                const chart = this.series.chart;

                // Check if sticky tooltip is active
                if ((chart as any).renderTo?.__bubbleComponentInstance?.stickyTooltipActive) {
                  return;
                }

                chart.series[0].points.forEach(point => {
                  point.update(
                    {
                      marker: {
                        fillColor: 'rgba(59, 152, 168, 0.7)' // reset all
                      }
                    },
                    false
                  );
                });

                chart.redraw();

                if (chart.tooltip) {
                  chart.tooltip.hide();
                }
              }
            }
          }
        }
      ];
    }

    setTimeout(() => {
      this.myChart = Highcharts.chart(this.chartContainerId, this.chartOptions);

      // Attach BubbleComponent instance to chart container for event access (for all chart types)
      if (this.myChart && (this.myChart as any).renderTo) {
        (this.myChart as any).renderTo.__bubbleComponentInstance = this;
      }

      // Patch tooltip.hide to prevent hiding when stickyTooltipActive is true (for all chart types)
      if (this.myChart?.tooltip) {
        const originalHide = this.myChart.tooltip.hide.bind(this.myChart.tooltip);
        (this.myChart.tooltip as any).originalHide = originalHide;
        this.myChart.tooltip.hide = () => {
          if (this.stickyTooltipActive) {
            // Do nothing if stickyTooltipActive
            return;
          }
          originalHide();
        };
      }

      // Listen for clicks to hide tooltip if not on selected bubble (for all chart types)
      this.outsideClickHandler = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (this.stickyTooltipActive) {
          let isBubble = false;
          // Check if clicked element is the selected bubble's SVG node
          if (this.myChart && (this.myChart as any).selectedBubble) {
            const selectedBubble = (this.myChart as any).selectedBubble;
            // Highcharts SVG point node
            if (selectedBubble?.graphic?.element) {
              if (selectedBubble.graphic.element === target || selectedBubble.graphic.element.contains(target)) {
                isBubble = true;
              }
            }
          }
          if (!isBubble) {
            if (this.stickyTooltipInterval) {
              clearInterval(this.stickyTooltipInterval);
              this.stickyTooltipInterval = null;
            }
            this.stickyTooltipActive = false;
            if (this.myChart?.tooltip && (this.myChart.tooltip as any).originalHide) {
              (this.myChart.tooltip as any).originalHide();
            }
            if (this.myChart && (this.myChart as any).selectedBubble) {
              (this.myChart as any).selectedBubble.select(false, false);
              (this.myChart as any).selectedBubble = null;
            }
          }
        }
      };
      document.addEventListener('mousedown', this.outsideClickHandler);

      document.addEventListener('click', this.handleTooltipClick.bind(this));
      this._cdref.detectChanges();
    }, 100);
  }

  public handleTooltipClick(event: Event) {
    const target = event.target as HTMLElement;
    if (target && target.classList.contains('adjust-link')) {
      // Get the currently hovered point data
      const hoveredPoint = this.myChart?.hoverPoint;
      if (hoveredPoint && this.config.onAdjustClick) {
        const bubbleData = hoveredPoint.options as BubbleDataPoint;
        this.config.onAdjustClick(bubbleData);
        if (this.myChart && this.myChart.tooltip) {
          this.myChart.tooltip.hide();
          if (this.type === 'Simulation Report') {
            if (this.myChart && (this.myChart as any).renderTo) {
              (this.myChart as any).renderTo.__bubbleComponentInstance = this;
            }
          }
        }
      }
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.handleTooltipClick.bind(this));
    if (this.outsideClickHandler) {
      document.removeEventListener('mousedown', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }

    if (this.stickyTooltipInterval) {
      clearInterval(this.stickyTooltipInterval);
      this.stickyTooltipInterval = null;
    }
    if (this.myChart) {
      this.myChart.destroy();
      this.myChart = null as any;
    }
  }
}
